<template>
  <div class="date-range-container">
    <form id="dateRangeForm" class="p-field">
      <template v-if="isRangeView">
        <div class="range p-d-flex">
          <div class="p-field p-mr-2">
            <label for="startDate">Start Date</label>
            <Calendar id="startDate" v-model="dateRangeStart" :disabled="isDisabled" :manualInput="false"
              :monthNavigator="true" :yearNavigator="true" :yearRange="$store.getters.datePickerYearRange"
              :showIcon="true" selectionMode="single" dateFormat="mm/dd/yy" />
          </div>
          <div class="p-field">
            <label for="endDate">End Date</label>
            <Calendar id="endDate" v-model="dateRangeEnd" :disabled="isDisabled" :manualInput="false"
              :monthNavigator="true" :yearNavigator="true" :yearRange="$store.getters.datePickerYearRange"
              :showIcon="true" selectionMode="single" dateFormat="mm/dd/yy" />
          </div>
        </div>
      </template>

      <template v-if="isMonthRangeView">
        <div class="month-range p-d-flex">
          <div class="p-field p-mr-2">
            <label for="monthRangeStart">Start Month</label>
            <Calendar id="monthRangeStart" ref="monthRangeStartCalendar" v-model="monthRangeStart" :disabled="isDisabled" :manualInput="false"
              :yearNavigator="true" :yearRange="$store.getters.datePickerYearRange"
              :showIcon="true" selectionMode="single" dateFormat="MM yy" view="month"
              @show="onMonthRangeStartShow" @year-change="onMonthRangeStartYearChange" @hide="onCalendarHide" />
          </div>
          <div class="p-field">
            <label for="monthRangeEnd">End Month</label>
            <Calendar id="monthRangeEnd" ref="monthRangeEndCalendar" v-model="monthRangeEnd" :disabled="isDisabled || !monthRangeStartSelected" :manualInput="false"
              :yearNavigator="true" :yearRange="$store.getters.datePickerYearRange"
              :showIcon="true" selectionMode="single" dateFormat="MM yy" view="month"
              @show="onMonthRangeEndShow" @year-change="onMonthRangeEndYearChange" @hide="onCalendarHide" />
          </div>
        </div>
      </template>

      <template v-if="!isRangeView && isMonthView">
        <label for="dateMonth">Month</label>
        <div class="month p-d-flex p-buttonset">
          <Button icon="pi pi-chevron-left" class="btn-previous-month p-button-outlined" :disabled="isDisabled"
            @click="onClickPreviousMonth" />
          <Calendar id="dateMonth" v-model="dateMonth" :disabled="isDisabled" :manualInput="false" :yearNavigator="true"
            :yearRange="$store.getters.datePickerYearRange" :showIcon="true" selectionMode="single" dateFormat="MM yy"
            view="month" />
          <Button icon="pi pi-chevron-right" class="btn-next-month p-button-outlined" :disabled="isDisabled"
            @click="onClickNextMonth" />
        </div>
      </template>

      <template v-if="!isRangeView && isDayView">
        <label for="dateDay">Day</label>
        <div class="day p-d-flex p-buttonset">
          <Button icon="pi pi-chevron-left" class="btn-previous-day p-button-outlined" :disabled="isDisabled"
            @click="onClickPreviousDay" />
          <Calendar id="dateDay" v-model="dateDay" :disabled="isDisabled" :manualInput="false" :yearNavigator="true"
            :yearRange="$store.getters.datePickerYearRange" :showIcon="true" selectionMode="single"
            dateFormat="D, MM d yy" view="date" />
          <Button icon="pi pi-chevron-right" class="btn-next-day p-button-outlined" :disabled="isDisabled"
            @click="onClickNextDay" />
        </div>
      </template>
    </form>
  </div>
</template>


<script lang="ts">
import Vue from 'vue'
import Calendar from 'primevue/calendar'

Vue.component('Calendar', Calendar)

export default Vue.extend({
  name: 'DateRangePicker',
  props: {
    isRangeView: {
      type: Boolean,
      default: false,
    },
    isMonthView: {
      type: Boolean,
      default: false,
    },
    isDayView: {
      type: Boolean,
      default: false,
    },
    isMonthRangeView: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      tempDateRange: [] as Date[],
      tempMonthRange: [null, null] as (Date | null)[],
      activeMonthObserver: null as MutationObserver | null,
      activeObserverConfig: null as { calendarRef: string; minDate: Date | null; maxDate: Date | null; year: number } | null,
      isApplyingDisabledState: false,
    }
  },
  computed: {
    dateRangeStart: {
      get(): Date[] {
        return this.$store.getters.currentDateRange[0]
      },
      set(newDate: Date) {
        this.tempDateRange[0] = newDate
        this.$store.dispatch('updateRangeViewsDateRange', { newRange: this.tempDateRange })
        this.$store.dispatch('updateCurrentDateRange', { newDateRange: this.tempDateRange })

        if (this.tempDateRange[0] > this.tempDateRange[1]) {
          const newEndDate = new Date(this.tempDateRange[0].getTime())
          newEndDate.setDate(this.tempDateRange[0].getDate() + 30)
          this.dateRangeEnd = newEndDate
        }
      },
    },
    dateRangeEnd: {
      get(): Date[] {
        return this.$store.getters.currentDateRange[1]
      },
      set(newDate: Date) {
        this.tempDateRange[1] = newDate
        this.$store.dispatch('updateRangeViewsDateRange', { newRange: this.tempDateRange })
        this.$store.dispatch('updateCurrentDateRange', { newDateRange: this.tempDateRange })

        if (this.tempDateRange[0] > this.tempDateRange[1]) {
          const newStartDate = new Date(this.tempDateRange[1].getTime())
          newStartDate.setDate(this.tempDateRange[0].getDate() - 30)
          this.dateRangeStart = newStartDate
        }
      },
    },
    dateMonth: {
      get(): Date {
        return this.$store.getters.currentDateRange[0]
      },
      set(newMonth: Date) {
        this.$store.dispatch('updateMonthViewDateRange', {
          newRange: [
            newMonth,
            new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0),
          ]
        })
        this.$store.dispatch('updateCurrentDateRange', {
          newDateRange: [
            newMonth,
            new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0),
          ]
        })
      },
    },
    dateDay: {
      get(): Date {
        return this.$store.getters.currentDateRange[0]
      },
      set(newDay: Date) {
        this.$store.dispatch('updateDayViewDateRange', { newRange: [newDay, newDay] })
        this.$store.dispatch('updateCurrentDateRange', { newDateRange: [newDay, newDay] })
      },
    },
    monthRangeStart: {
      get(): Date | null {
        return this.tempMonthRange[0] ?? null
      },
      set(newMonth: Date) {
        // Set to first day of selected month at 12:00 AM
        const startOfMonth = new Date(newMonth.getFullYear(), newMonth.getMonth(), 1, 0, 0, 0, 0)
        let newEndMonth = this.tempMonthRange[1]
        // Adjust end month if it's now invalid (before start or more than 1 year after)
        if (newEndMonth) {
          const startTime = startOfMonth.getTime()
          const endTime = newEndMonth.getTime()
          const oneYearLater = new Date(startOfMonth.getFullYear() + 1, startOfMonth.getMonth(), 1).getTime()
          if (endTime < startTime || endTime >= oneYearLater) {
            // Set end to 1 year after start month (last day of that month at 11:59 PM)
            newEndMonth = new Date(startOfMonth.getFullYear() + 1, startOfMonth.getMonth(), 0, 23, 59, 59, 999)
          }
        }
        // Replace entire array to ensure reactivity
        this.tempMonthRange = [startOfMonth, newEndMonth]
        this.updateMonthRangeStore()
      },
    },
    monthRangeEnd: {
      get(): Date | null {
        return this.tempMonthRange[1] ?? null
      },
      set(newMonth: Date) {
        // Set to last day of selected month at 11:59 PM
        const endOfMonth = new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0, 23, 59, 59, 999)
        let newStartMonth = this.tempMonthRange[0]
        // Adjust start month if it's now invalid (after end or more than 1 year before)
        if (newStartMonth) {
          const startTime = newStartMonth.getTime()
          const endTime = endOfMonth.getTime()
          const oneYearEarlier = new Date(endOfMonth.getFullYear() - 1, endOfMonth.getMonth() + 1, 1, 0, 0, 0, 0).getTime()
          if (startTime > endTime || startTime < oneYearEarlier) {
            // Set start to 1 year before end month (first day at 12:00 AM)
            newStartMonth = new Date(endOfMonth.getFullYear() - 1, endOfMonth.getMonth() + 1, 1, 0, 0, 0, 0)
          }
        }
        // Replace entire array to ensure reactivity
        this.tempMonthRange = [newStartMonth, endOfMonth]
        this.updateMonthRangeStore()
      },
    },
    monthRangeStartSelected(): boolean {
      return this.tempMonthRange[0] != null
    },
    monthRangeEndMinDate(): Date | null {
      if (!this.tempMonthRange[0]) return null
      return new Date(this.tempMonthRange[0].getFullYear(), this.tempMonthRange[0].getMonth(), 1)
    },
    monthRangeEndMaxDate(): Date | null {
      if (!this.tempMonthRange[0]) return null
      // Maximum 1 year (12 months) from start month
      return new Date(this.tempMonthRange[0].getFullYear() + 1, this.tempMonthRange[0].getMonth(), 0)
    },
    yearRangeMinDate(): Date | null {
      const yearRange = this.$store.getters.datePickerYearRange as string
      if (!yearRange) return null
      const minYear = parseInt(yearRange.split(':')[0], 10)
      return new Date(minYear, 0, 1) // January 1st of min year
    },
    yearRangeMaxDate(): Date | null {
      const yearRange = this.$store.getters.datePickerYearRange as string
      if (!yearRange) return null
      const maxYear = parseInt(yearRange.split(':')[1], 10)
      return new Date(maxYear, 11, 31) // December 31st of max year
    },
    monthRangeEndMaxDateConstrained(): Date | null {
      const rangeMax = this.monthRangeEndMaxDate
      const yearMax = this.yearRangeMaxDate
      if (!rangeMax && !yearMax) return null
      if (!rangeMax) return yearMax
      if (!yearMax) return rangeMax
      // Return the earlier of the two dates
      return rangeMax.getTime() < yearMax.getTime() ? rangeMax : yearMax
    },
  },
  watch: {
    '$store.getters.currentDateRange': {
      immediate: true,
      deep: true,
      handler(newRange: Date[]) {
        if (this.isMonthRangeView && newRange && newRange.length === 2 && newRange[0] && newRange[1]) {
          // Only update if tempMonthRange is not set or if dates are significantly different
          const currentStart = this.tempMonthRange[0]
          const currentEnd = this.tempMonthRange[1]
          const needsUpdate = !currentStart || !currentEnd ||
            currentStart.getFullYear() !== newRange[0].getFullYear() ||
            currentStart.getMonth() !== newRange[0].getMonth() ||
            currentEnd.getFullYear() !== newRange[1].getFullYear() ||
            currentEnd.getMonth() !== newRange[1].getMonth()
          if (needsUpdate) {
            this.tempMonthRange = [new Date(newRange[0]), new Date(newRange[1])]
          }
        }
      },
    },
  },
  mounted: function () {
    this.tempDateRange = this.$store.getters.currentDateRange
  },
  beforeDestroy: function () {
    // Clean up observer when component is destroyed
    if (this.activeMonthObserver) {
      this.activeMonthObserver.disconnect()
      this.activeMonthObserver = null
    }
  },
  methods: {
    updateMonthRangeStore: function () {
      if (this.tempMonthRange[0] && this.tempMonthRange[1]) {
        const startDate = new Date(this.tempMonthRange[0].getFullYear(), this.tempMonthRange[0].getMonth(), 1)
        const endDate = new Date(this.tempMonthRange[1].getFullYear(), this.tempMonthRange[1].getMonth() + 1, 0)
        this.$store.dispatch('updateMonthRangeViewsDateRange', { newRange: [startDate, endDate] })
        this.$store.dispatch('updateCurrentDateRange', { newDateRange: [startDate, endDate] })
      }
    },
    onClickPreviousMonth: function () {
      this.dateMonth = new Date(
        this.dateMonth.getFullYear(),
        this.dateMonth.getMonth() - 1,
        1
      )
    },
    onClickNextMonth: function () {
      this.dateMonth = new Date(
        this.dateMonth.getFullYear(),
        this.dateMonth.getMonth() + 1,
        1
      )
    },
    onClickPreviousDay: function () {
      this.dateDay = new Date(
        this.dateDay.getFullYear(),
        this.dateDay.getMonth(),
        this.dateDay.getDate() - 1
      )
    },
    onClickNextDay: function () {
      this.dateDay = new Date(
        this.dateDay.getFullYear(),
        this.dateDay.getMonth(),
        this.dateDay.getDate() + 1,
        1
      )
    },
    updateDisabledMonths(calendarRef: string, minDate: Date | null, maxDate: Date | null, year?: number): void {
      // Disconnect any existing observer
      if (this.activeMonthObserver) {
        this.activeMonthObserver.disconnect()
        this.activeMonthObserver = null
      }

      const calendar = this.$refs[calendarRef] as (Vue & { currentYear: number; $el: HTMLElement }) | undefined
      if (!calendar) return

      // Get current year from parameter or from the calendar component's internal state
      const currentYear = year ?? calendar.currentYear

      // Store config for observer callback
      this.activeObserverConfig = { calendarRef, minDate, maxDate, year: currentYear }

      const applyDisabledState = (panel: Element, yearToUse: number): void => {
        const monthCells = panel.querySelectorAll('.p-monthpicker-month')
        if (monthCells.length === 0) return

        // Set flag to prevent observer from re-triggering
        this.isApplyingDisabledState = true

        // Pre-compute min/max year and month for comparison
        const minYear = minDate ? minDate.getFullYear() : null
        const minMonth = minDate ? minDate.getMonth() : null
        const maxYear = maxDate ? maxDate.getFullYear() : null
        const maxMonth = maxDate ? maxDate.getMonth() : null

        monthCells.forEach((cell: Element, monthIndex: number) => {
          // Compare year and month explicitly to avoid Date comparison issues
          const isBeforeMin = minYear !== null && minMonth !== null &&
            (yearToUse < minYear || (yearToUse === minYear && monthIndex < minMonth))
          const isAfterMax = maxYear !== null && maxMonth !== null &&
            (yearToUse > maxYear || (yearToUse === maxYear && monthIndex > maxMonth))
          const isDisabled = isBeforeMin || isAfterMax

          if (isDisabled) {
            cell.classList.add('p-disabled')
            ;(cell as HTMLElement).style.pointerEvents = 'none'
          } else {
            cell.classList.remove('p-disabled')
            ;(cell as HTMLElement).style.pointerEvents = ''
          }
        })

        // Clear flag after a microtask to ensure all DOM updates are processed
        Promise.resolve().then(() => {
          this.isApplyingDisabledState = false
        })
      }

      const findAndApply = (): void => {
        // Find the visible month picker panel
        let panel: Element | null = null
        const allPanels = document.querySelectorAll('.p-datepicker')

        for (let i = allPanels.length - 1; i >= 0; i--) {
          const p = allPanels[i] as HTMLElement
          const monthpicker = p.querySelector('.p-monthpicker')
          if (monthpicker && window.getComputedStyle(p).display !== 'none') {
            panel = p
            break
          }
        }

        if (!panel) {
          return
        }

        // Apply initial disabled state
        applyDisabledState(panel, currentYear)

        // Track the last known year to detect changes
        let lastKnownYear = currentYear

        // Helper to get current year from the panel
        const getYearFromPanel = (): number => {
          // Try select element first
          const yearSelect = panel?.querySelector('.p-datepicker-year') as HTMLSelectElement | null
          if (yearSelect?.value) {
            return parseInt(yearSelect.value, 10)
          }
          // Try text content
          if (yearSelect?.textContent) {
            const parsed = parseInt(yearSelect.textContent.trim(), 10)
            if (!isNaN(parsed)) return parsed
          }
          // Fallback to stored config
          return this.activeObserverConfig?.year ?? currentYear
        }

        // Set up MutationObserver to re-apply when PrimeVue modifies the DOM
        const observer = new MutationObserver(() => {
          // Skip if we're the ones making changes
          if (this.isApplyingDisabledState) return
          if (!this.activeObserverConfig) return

          // Always check the current year from the DOM
          const detectedYear = getYearFromPanel()

          // Only re-apply if year changed
          if (detectedYear !== lastKnownYear) {
            lastKnownYear = detectedYear
            // Update the stored config with the new year
            this.activeObserverConfig.year = detectedYear
            applyDisabledState(panel as Element, detectedYear)
          }
        })

        observer.observe(panel, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        })

        this.activeMonthObserver = observer
      }

      // Use Vue.nextTick + requestAnimationFrame to ensure DOM has been updated and painted
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          findAndApply()
        })
      })
    },
    onMonthRangeStartShow(): void {
      this.updateDisabledMonths('monthRangeStartCalendar', this.yearRangeMinDate, this.yearRangeMaxDate)
    },
    onMonthRangeStartYearChange(event: { month: number; year: number }): void {
      this.updateDisabledMonths('monthRangeStartCalendar', this.yearRangeMinDate, this.yearRangeMaxDate, event.year)
    },
    onMonthRangeEndShow(): void {
      this.updateDisabledMonths('monthRangeEndCalendar', this.monthRangeEndMinDate, this.monthRangeEndMaxDateConstrained)
    },
    onMonthRangeEndYearChange(event: { month: number; year: number }): void {
      this.updateDisabledMonths('monthRangeEndCalendar', this.monthRangeEndMinDate, this.monthRangeEndMaxDateConstrained, event.year)
    },
    onCalendarHide(): void {
      // Clean up observer when calendar closes
      if (this.activeMonthObserver) {
        this.activeMonthObserver.disconnect()
        this.activeMonthObserver = null
      }
      this.activeObserverConfig = null
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.date-range-container {
  .range,
  .month-range {
    .p-inputtext {
      padding-right: 2.4rem;
    }
  }

  .month,
  .day {
    .p-datepicker-trigger {
      display: none;
    }
  }

  .btn-previous-month,
  .btn-next-month,
  .btn-previous-day,
  .btn-next-day {
    width: 3.2rem;
  }

  .btn-previous-month,
  .btn-previous-day {
    border-right: 0;

    &:hover {
      border-right: 0 !important;
    }
  }

  .btn-next-month,
  .btn-next-day {
    border-left: 0;

    &:hover {
      border-left: 0 !important;
    }
  }

  .p-datepicker-trigger {
    margin-left: -2.4rem;
    background: transparent;
    border: none;

    &:hover {
      background: transparent;
      border: none;
    }

    .p-button-icon {
      font-size: 1.2rem;
      color: #000;
    }
  }

  .p-inputtext {
    @include font-bold;
    color: #000;
  }

  .p-monthpicker-month.p-disabled {
    opacity: 0.4;
    pointer-events: none;
    cursor: default;
  }
}
</style>
